import axios from "axios";
import axiosInstance from "@/helpers/axiosInstance";
import type { UserNotificationSettingUpdate } from "./UserService";

export interface AgentProfileHeader {
    userId?: string | null;
    isFollowing?: boolean;
    followerCount?: number;
    displayName?: string | null;
    nickname?: string | null;
    brokerage: string | null;
    bio?: string | null;
    primaryLink?: string | null;
    phoneNumber?: string | null;
    socialProfiles: {
        instagram?: string | null;
        tiktok?: string | null;
        xTwitter?: string | null;
        facebook?: string | null;
        youtube?: string | null;
    };
    profilePhotoUrl?: string | null;
    profilePrivate: boolean;
    showScoreLeaderboard?: boolean;
    notificationSettings?: UserNotificationSettingUpdate[];
}

export interface AgentStats {
    playablePropertiesCount: number;
    userGuessCount: number;
    profilePrivate: boolean;
}

const apiEndpoint = "/api/agents";
const followsApiEndpoint = "/api/follows";

/**
 * Fetches an agent profile header by identifier (UUID or nickname).
 */
export async function getAgentProfileHeader(
    agentIdentifier: string,
    options: { requestingUserId?: string } = {},
): Promise<AgentProfileHeader> {
    const encoded = encodeURIComponent(agentIdentifier.trim());
    const params = options.requestingUserId ? { requestingUserId: options.requestingUserId } : undefined;
    const res = await axios.get<AgentProfileHeader>(`${apiEndpoint}/${encoded}/profile/header`, { params });
    return res.data;
}

/**
 * Fetches aggregate stats for an agent profile by identifier (UUID or nickname).
 */
export async function getAgentProfileStats(agentIdentifier: string): Promise<AgentStats> {
    const encoded = encodeURIComponent(agentIdentifier.trim());
    const res = await axios.get<AgentStats>(`${apiEndpoint}/${encoded}/profile/stats`);
    return res.data;
}

export async function followAgent(followerUserId: string, followedUserId: string): Promise<{ message: string }> {
    const res = await axiosInstance.post<{ message: string }>(followsApiEndpoint, {
        followerUserId,
        followedUserId,
    });
    return res.data;
}

export async function unfollowAgent(followerUserId: string, followedUserId: string): Promise<{ message: string }> {
    const res = await axiosInstance.delete<{ message: string }>(followsApiEndpoint, {
        data: {
            followerUserId,
            followedUserId,
        },
    });
    return res.data;
}

export async function getFollowersCount(userId: string): Promise<number> {
    const res = await axiosInstance.get<{ count: number }>(`/api/users/${encodeURIComponent(userId)}/followers`, {
        params: { page: 1, limit: 1 },
    });
    return Number(res.data?.count ?? 0);
}

